import { QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { RQAPICallWithData } from 'store/APIClient';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { transformUrlParams } from 'helpers/urlHelpers';
import { snakify } from 'helpers/syntaxHelper';
import { globalFiltersPostProcessingMiddleware } from '../../../../shared/Filters/filtersMiddlewares';
import {
    UpdateFilters,
    getUpdatedFilters,
} from 'routes/BudgetModule/components/shared/Filters/helpers/filtersHelpers';
import { useCallback } from 'react';
import { trackEventEditAnalyseFilter } from '../helpers/trackersHelpers';
import { useAddScopesToAnalyzeFilters } from 'routes/BudgetModule/helpers/hooks/useAddScopesToAnalyzeFilters';

/**
 * Custom hook fetching Analyse filters and provides appropriate update method.
 */
export const useGetAnalyseFilters = ({
    budgetProposal,
    resourceSoftwareId,
}: {
    budgetProposal: BudgetProposalT;
    resourceSoftwareId: string;
}): {
    filters: SentenceFilterT[];
    updateFilters: UpdateFilters;
    areFetchingFilters: boolean;
} => {
    const queryKey: QueryKey = [
        'budgetGlobalFilters',
        budgetProposal.id,
        resourceSoftwareId,
    ];
    const { data = [], isFetching } = useQuery({
        queryKey: queryKey,

        queryFn: async () => {
            const response = await RQAPICallWithData<
                SentenceFilterWithScopesT[]
            >({
                endpoint: `budget_filters/${budgetProposal.id}?is_overview=false&resource_software_id=${resourceSoftwareId}`,
                method: 'GET',
                preProcessingUrlCallback: (url) =>
                    transformUrlParams(url, ['filter_field'], snakify),
                postProcessingCallback: globalFiltersPostProcessingMiddleware,
            });
            return response.content.data;
        },
    });

    const filtersWithScope = useAddScopesToAnalyzeFilters(data);

    const queryClient = useQueryClient();
    // useCallback avoids costly rerendering of BudgetFilters when updating one of Analyse grids' settings
    const updateFilters = useCallback(
        (newFilterData: Partial<SentenceFilterT>) => {
            trackEventEditAnalyseFilter(newFilterData);
            queryClient.setQueryData(queryKey, (oldData: SentenceFilterT[]) =>
                getUpdatedFilters(oldData, newFilterData),
            );
        },
        [JSON.stringify(queryKey), queryClient],
    );
    return {
        filters: filtersWithScope,
        updateFilters,
        areFetchingFilters: isFetching,
    };
};
